
import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { Project, AnalysisCatalogItem } from '../../types';
import { Plus, Trash2, X, Briefcase, Loader2, Eye, EyeOff, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const AdminProjects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [catalogItems, setCatalogItems] = useState<AnalysisCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
      name: '',
      code: '',
      description: '',
      default_analyses: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    if (!isSupabaseConfigured()) {
        setProjects([
            { id: '1', name: 'TB-Speed', code: 'TBS', description: 'Étude pédiatrique', default_analyses: ['1', '2'], created_at: '', active: true }
        ]);
        setCatalogItems([
            { id: '1', name: 'Microscopie', created_at: '' },
            { id: '2', name: 'GeneXpert', created_at: '' },
            { id: '3', name: 'Culture', created_at: '' }
        ]);
        setLoading(false);
        return;
    }

    const [projRes, catRes] = await Promise.all([
        supabase.from('projects').select('*').order('name'),
        supabase.from('analysis_catalog').select('*').order('name')
    ]);

    if (projRes.data) setProjects(projRes.data as Project[]);
    if (catRes.data) setCatalogItems(catRes.data as AnalysisCatalogItem[]);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
      if (!window.confirm("Supprimer ce projet ?")) return;
      if (isSupabaseConfigured()) {
          const { error } = await supabase.from('projects').delete().eq('id', id);
          if (error) alert("Erreur: " + error.message);
          else fetchData();
      } else {
          setProjects(projects.filter(p => p.id !== id));
      }
  };

  const toggleProjectStatus = async (project: Project) => {
      const newStatus = project.active === false ? true : false;
      if (!isSupabaseConfigured()) {
          setProjects(prev => prev.map(p => p.id === project.id ? { ...p, active: newStatus } : p));
          return;
      }
      const { error } = await supabase.from('projects').update({ active: newStatus }).eq('id', project.id);
      if (error) alert("Erreur: " + error.message);
      else fetchData();
  };

  const handleSave = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      const payload = {
          name: formData.name,
          code: formData.code,
          description: formData.description,
          default_analyses: formData.default_analyses,
          active: true
      };

      if (isSupabaseConfigured()) {
          const { error } = await supabase.from('projects').insert([payload]);
          if (error) alert("Erreur: " + error.message);
          else {
              fetchData();
              closeModal();
          }
      } else {
          setProjects([...projects, { id: Math.random().toString(), ...payload, created_at: '' }]);
          closeModal();
      }
      setIsSubmitting(false);
  };

  const toggleAnalysis = (id: string) => {
      if (formData.default_analyses.includes(id)) {
          setFormData({ ...formData, default_analyses: formData.default_analyses.filter(i => i !== id) });
      } else {
          setFormData({ ...formData, default_analyses: [...formData.default_analyses, id] });
      }
  };

  const closeModal = () => {
      setFormData({ name: '', code: '', description: '', default_analyses: [] });
      setIsModalOpen(false);
  };

  const filteredProjects = projects.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (p.code || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const currentProjects = filteredProjects.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
      <div className="flex flex-col h-full overflow-hidden">
          {/* BARRE DE FILTRES FIXE */}
          <div className="p-4 bg-white border-b border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
              <div className="relative flex-1 max-w-md w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                      type="text" 
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      placeholder="Rechercher un projet..." 
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
              </div>

              <div className="flex items-center gap-4">
                 {filteredProjects.length > 0 && (
                    <div className="flex items-center gap-2">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600 transition-colors">
                            <ChevronLeft size={20} />
                        </button>
                        <span className="text-sm font-medium whitespace-nowrap px-2">
                            {currentPage} / {totalPages}
                        </span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600 transition-colors">
                            <ChevronRight size={20} />
                        </button>
                    </div>
                 )}
                 <button onClick={() => setIsModalOpen(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2 shadow-sm">
                    <Plus size={16}/> Nouveau Projet
                 </button>
              </div>
          </div>

          {/* TABLEAU SCROLLABLE */}
          <div className="flex-1 overflow-auto bg-white">
              <table className="w-full text-left text-sm text-slate-600 border-collapse">
                  <thead className="sticky top-0 z-10 bg-slate-100 text-slate-900 font-bold border-b-2 border-slate-200 shadow-sm">
                      <tr>
                          <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Nom du Projet</th>
                          <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Code</th>
                          <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Description</th>
                          <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Analyses par défaut</th>
                          <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Statut</th>
                          <th className="px-6 py-4 uppercase tracking-wider text-[11px] text-right">Actions</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                      {loading ? (
                          <tr><td colSpan={6} className="p-8 text-center text-slate-400">Chargement...</td></tr>
                      ) : currentProjects.map(project => (
                          <tr key={project.id} className={`hover:bg-slate-50 transition-colors ${project.active === false ? 'opacity-60 bg-slate-50' : ''}`}>
                              <td className="px-6 py-4 font-bold text-slate-800 flex items-center gap-2">
                                  <Briefcase size={16} className={project.active === false ? 'text-slate-400' : 'text-indigo-600'} />
                                  {project.name}
                              </td>
                              <td className="px-6 py-4 font-mono text-xs">{project.code}</td>
                              <td className="px-6 py-4 text-xs text-slate-500 max-w-xs truncate" title={project.description || ''}>
                                  {project.description || "-"}
                              </td>
                              <td className="px-6 py-4">
                                  <div className="flex flex-wrap gap-1">
                                      {project.default_analyses && project.default_analyses.length > 0 ? (
                                          project.default_analyses.map(id => {
                                              const item = catalogItems.find(i => i.id === id);
                                              return item ? (
                                                  <span key={id} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-medium rounded border border-indigo-100">
                                                      {item.name}
                                                  </span>
                                              ) : null;
                                          })
                                      ) : (
                                          <span className="text-xs text-slate-400 italic">Aucune</span>
                                      )}
                                  </div>
                              </td>
                              <td className="px-6 py-4">
                                  {project.active !== false ? (
                                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-bold">Actif</span>
                                  ) : (
                                      <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded-full font-bold">Inactif</span>
                                  )}
                              </td>
                              <td className="px-6 py-4 text-right">
                                  <div className="flex justify-end gap-2">
                                      <button onClick={() => toggleProjectStatus(project)} className={`p-1.5 rounded transition-colors ${project.active === false ? 'text-slate-400 hover:text-green-600 hover:bg-green-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}>
                                          {project.active === false ? <EyeOff size={16} /> : <Eye size={16} />}
                                      </button>
                                      <button onClick={() => handleDelete(project.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                                          <Trash2 size={16} />
                                      </button>
                                  </div>
                              </td>
                          </tr>
                      ))}
                      {currentProjects.length === 0 && !loading && (
                          <tr><td colSpan={6} className="p-8 text-center text-slate-400">Aucun projet trouvé</td></tr>
                      )}
                  </tbody>
              </table>
          </div>

          {isModalOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col animate-in fade-in zoom-in-95">
                      <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                          <h3 className="font-bold text-lg text-slate-800">Nouveau Projet</h3>
                          <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                              <X size={24} />
                          </button>
                      </div>
                      <div className="p-6 overflow-y-auto max-h-[80vh]">
                          <form onSubmit={handleSave} className="space-y-4">
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom du Projet</label>
                                  <input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Ex: TB-Speed" className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none" />
                              </div>
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Code (Court)</label>
                                  <input required value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value})} placeholder="Ex: TBS" className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none font-mono uppercase" />
                              </div>
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                                  <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none h-20" />
                              </div>
                              <div className="border-t border-slate-200 pt-4">
                                  <label className="block text-sm font-medium text-slate-700 mb-2">Analyses Automatiques</label>
                                  <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50 space-y-1">
                                      {catalogItems.map(item => (
                                          <label key={item.id} className="flex items-center gap-2 p-2 hover:bg-white hover:shadow-sm rounded cursor-pointer">
                                              <input type="checkbox" checked={formData.default_analyses.includes(item.id)} onChange={() => toggleAnalysis(item.id)} className="rounded text-indigo-600 focus:ring-indigo-500" />
                                              <span className="text-sm text-slate-700">{item.name}</span>
                                          </label>
                                      ))}
                                  </div>
                              </div>
                              <div className="flex justify-end gap-3 pt-2">
                                  <button type="button" onClick={closeModal} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium">Annuler</button>
                                  <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2">
                                      {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                                      Enregistrer
                                  </button>
                              </div>
                          </form>
                      </div>
                  </div>
              </div>
          )}
      </div>
  );
};

export default AdminProjects;
